import { walletService } from "../services/walletService";
import { appStore } from "../state/store";
import { byId } from "./dom";
import { createStepController } from "./steps";
import { formatNaira, toKobo } from "./format";
import { config } from "../config";

export interface FundWalletFlow {
  open(): void;
  close(): void;
}

export function initFundWalletFlow(): FundWalletFlow {
  const walletCard = byId("wallet-card");
  const formRoot = byId("wallet-fund-form");
  const steps = createStepController(formRoot);

  const amountInput = byId<HTMLInputElement>("wallet-fund-amount");
  const amountError = byId("wallet-fund-amount-error");
  const continueBtn = byId<HTMLButtonElement>("wallet-fund-continue-btn");
  const cancelBtn = byId("wallet-fund-cancel");

  const summaryAmount = byId("wallet-fund-summary-amount");
  const payBtn = byId<HTMLButtonElement>("wallet-fund-pay-btn");
  const backBtn = byId("wallet-fund-back-btn");

  const successAmount = byId("wallet-fund-success-amount");
  const successReference = byId("wallet-fund-success-reference");
  const successNewBalance = byId("wallet-fund-success-balance");
  const doneBtn = byId("wallet-fund-done-btn");

  const failureReason = byId("wallet-fund-failure-reason");
  const retryBtn = byId("wallet-fund-retry-btn");
  const cancelFromFailBtn = byId("wallet-fund-cancel-2");

  let amountKobo = 0;

  function reset(): void {
    amountInput.value = "";
    amountError.hidden = true;
    steps.show("amount");
  }

  function open(): void {
    reset();
    formRoot.hidden = false;
    walletCard.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    amountInput.focus();
  }

  function close(): void {
    formRoot.hidden = true;
  }

  continueBtn.addEventListener("click", () => {
    const naira = Number(amountInput.value);
    if (!naira || naira < 100) {
      amountError.textContent = "Enter at least \u20a6100.";
      amountError.hidden = false;
      amountInput.focus();
      return;
    }
    amountError.hidden = true;
    amountKobo = toKobo(naira);
    summaryAmount.textContent = `\u20a6${formatNaira(amountKobo)}`;
    steps.show("summary");
  });

  backBtn.addEventListener("click", () => steps.show("amount"));
  cancelBtn.addEventListener("click", close);
  cancelFromFailBtn.addEventListener("click", close);

  payBtn.addEventListener("click", async () => {
    steps.show("pending");
    // Real mode: hand over to OPay's secure payment page. The wallet is only credited once OPay
    // confirms, which the app checks when the person comes back (see topupReturn.ts).
    if (!config.useMockServices) {
      try {
        const { cashierUrl } = await walletService.startTopup(amountKobo);
        window.location.href = cashierUrl;
      } catch (err) {
        failureReason.textContent = err instanceof Error ? err.message : "Something went wrong. Please try again.";
        steps.show("failed");
      }
      return;
    }
    try {
      const result = await walletService.fundWallet({ amountKobo });
      if (result.status === "success" && result.newBalanceKobo !== undefined) {
        appStore.setWallet({
          estateId: appStore.state.wallet?.estateId ?? "",
          balanceKobo: result.newBalanceKobo,
          currency: "NGN",
          updatedAt: new Date().toISOString(),
        });
        appStore.prependTransaction({
          id: result.reference,
          type: "wallet_funding",
          status: "success",
          amountKobo: result.amountKobo,
          direction: "credit",
          description: "Wallet Funding",
          reference: result.reference,
          createdAt: new Date().toISOString(),
        });
        successAmount.textContent = `\u20a6${formatNaira(result.amountKobo)}`;
        successReference.textContent = result.reference;
        successNewBalance.textContent = `\u20a6${formatNaira(result.newBalanceKobo)}`;
        steps.show("success");
      } else {
        failureReason.textContent = result.failureReason ?? "Payment could not be completed.";
        steps.show("failed");
      }
    } catch {
      failureReason.textContent = "Something went wrong. Please try again.";
      steps.show("failed");
    }
  });

  retryBtn.addEventListener("click", () => steps.show("summary"));
  doneBtn.addEventListener("click", close);

  reset();

  return { open, close };
}
